import {
  computeInvoiceTotals,
  validateInvoice,
  type InvoiceIssue,
  type InvoiceTotals,
} from "@stb/jurisdiction-au/invoice"

import {
  buildInvoiceDocument,
  SELLER_NAME_PLACEHOLDER,
  type InvoiceDocumentData,
} from "@/invoicing/document"
import { addDays } from "@/invoicing/editor-model"
import { parseAmountMinor, parseQuantityMilli } from "@/invoicing/format"

/**
 * Pure model of the free /invoice-generator (T401). Every number and rule comes from
 * `@stb/jurisdiction-au` (`computeInvoiceTotals`, `validateInvoice`) and the same document builder
 * the logged-in invoicing module (T412) uses; nothing here knows what GST is. No server calls.
 */

export interface GeneratorLine {
  /** Stable React key; never shown. */
  key: string
  description: string
  quantity: string
  unitAmount: string
  gstFree: boolean
}

export interface GeneratorForm {
  sellerName: string
  sellerAbn: string
  gstRegistered: boolean
  buyerName: string
  buyerAbn: string
  number: string
  issueDate: string
  dueDate: string
  amountsAre: "inclusive" | "exclusive"
  lines: GeneratorLine[]
}

export function emptyGeneratorForm(today: string): GeneratorForm {
  return {
    sellerName: "",
    sellerAbn: "",
    gstRegistered: true,
    buyerName: "",
    buyerAbn: "",
    number: "0001",
    issueDate: today,
    dueDate: addDays(today, 14),
    amountsAre: "exclusive",
    lines: [{ key: "line-0", description: "", quantity: "1", unitAmount: "", gstFree: false }],
  }
}

export type GeneratorProblemField = "number" | "issueDate" | "dueDate"

export interface GeneratorResult {
  document: InvoiceDocumentData
  totals: InvoiceTotals
  /** What the user must fix before the PDF is worth printing. */
  issues: InvoiceIssue[]
  /** Line key -> the field that could not be read. */
  lineErrors: Record<string, "quantity" | "amount">
  /** Header fields the paper invoice needs but the shared rules do not check. */
  fieldErrors: Partial<Record<GeneratorProblemField, string>>
}

const dayFormat = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
})

/** `2026-10-27` -> `27 Oct 2026`; anything unreadable is returned as typed. */
function formatDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? date : dayFormat.format(parsed)
}

const orNull = (value: string) => value.trim() || null

export function generate(form: GeneratorForm): GeneratorResult {
  const lineErrors: GeneratorResult["lineErrors"] = {}
  const lines = form.lines.map((line) => {
    const quantityMilli = parseQuantityMilli(line.quantity)
    const unitAmountMinor = parseAmountMinor(line.unitAmount)
    if (quantityMilli === null) lineErrors[line.key] = "quantity"
    else if (unitAmountMinor === null) lineErrors[line.key] = "amount"
    return {
      quantityMilli: quantityMilli ?? 0,
      unitAmountMinor: unitAmountMinor ?? 0,
      taxCode: line.gstFree ? ("GST_FREE" as const) : ("GST" as const),
    }
  })

  const totals = computeInvoiceTotals(lines, {
    amountsAre: form.amountsAre,
    // ponytail: one rounding method; the app offers per-line too
    rounding: "per-document",
    gstRegistered: form.gstRegistered,
  })
  const documentTitle = form.gstRegistered ? "tax_invoice" : "invoice"
  const buyer = { name: orNull(form.buyerName), abn: orNull(form.buyerAbn) }

  const document = buildInvoiceDocument({
    seller: {
      businessName: form.sellerName.trim() || SELLER_NAME_PLACEHOLDER,
      abn: orNull(form.sellerAbn),
      gstRegistered: form.gstRegistered,
      addressLines: [],
      bank: { accountName: null, bsb: null, accountNumber: null },
    },
    buyer: { ...buyer, address: null },
    number: orNull(form.number),
    documentTitle,
    issueDate: formatDate(form.issueDate),
    dueDate: formatDate(form.dueDate),
    amountsAre: form.amountsAre,
    note: null,
    showBankDetails: false,
    lines: form.lines.map((line, index) => ({
      description: line.description.trim() || "Description",
      quantityMilli: lines[index]!.quantityMilli,
      unitAmountMinor: lines[index]!.unitAmountMinor,
      taxCode: lines[index]!.taxCode,
      netMinor: totals.lines[index]!.netMinor,
      gstMinor: totals.lines[index]!.gstMinor,
      grossMinor: totals.lines[index]!.grossMinor,
    })),
    subtotalMinor: totals.subtotalMinor,
    gstMinor: totals.gstMinor,
    totalMinor: totals.totalMinor,
    roundingAdjustmentMinor: totals.roundingAdjustmentMinor,
  })

  const issues = validateInvoice({
    documentTitle,
    totalMinor: totals.totalMinor,
    sellerName: form.sellerName,
    sellerAbn: form.sellerAbn,
    buyerName: buyer.name,
    buyerAbn: buyer.abn,
    lineCount: form.lines.length,
  })

  const fieldErrors: GeneratorResult["fieldErrors"] = {}
  if (!form.number.trim()) fieldErrors.number = "Add an invoice number."
  if (Number.isNaN(Date.parse(`${form.issueDate}T00:00:00Z`))) {
    fieldErrors.issueDate = "Choose the issue date."
  }
  if (Number.isNaN(Date.parse(`${form.dueDate}T00:00:00Z`))) {
    fieldErrors.dueDate = "Choose the due date."
  } else if (!fieldErrors.issueDate && form.dueDate < form.issueDate) {
    fieldErrors.dueDate = "The due date cannot be before the issue date."
  }

  return { document, totals, issues, lineErrors, fieldErrors }
}
